import { SerdeDtoClass } from '@zaoblako/shared/modules/serde/classes/serde-dto.class';
import { TaskDtoKeysEnum } from '@zaoblako/shared/modules/task/enum/task-dto-keys.enum';
import { DtoMessageEnum } from '@zaoblako/shared/common/enums/dto.message.enum';
import { entityLikeSchema } from '@zaoblako/shared/modules/serde/schemas/entity-like.schema';
import { ClientCheckerKeysEnum } from '@zaoblako/shared/modules/client/enums/client-checker-keys.enum';
import { CatalogCheckersEnum } from '@zaoblako/shared/modules/catalog/enums/catalog-checkers.enum';
import { BusinessCheckerKeysEnum } from '@zaoblako/shared/modules/business/enum/business-checker-keys.enum';
import { SecurityBackendValidatorsEnum } from '@zaoblako/shared/modules/security/enums/security-backend-validators.enum';
import { DocumentCheckersEnum } from '@zaoblako/shared/modules/document/enums/document-checkers.enum';
import type { CompanyFindInterface } from '@zaoblako/shared/modules/client/dtos/company/find/company-find.dto';
import type { DirectionFindInterface } from '@zaoblako/shared/modules/catalog/dtos/direction/direction-find.dto';
import type { FilialFindInterface } from '@zaoblako/shared/modules/catalog/dtos/filial/filial-find.dto';
import type { UnitFindInterface } from '@zaoblako/shared/modules/catalog/dtos/unit/unit-find.dto';
import type { DealFindInterface } from '@zaoblako/shared/modules/business/dto/deal/deal-find.dto';
import type { ContragentFindInterface } from '@zaoblako/shared/modules/client/dtos/contragent/contragent-find.dto';
import type { UserFindInterface } from '@zaoblako/shared/modules/security/dtos/user/user-find.dto';
import type { UserGroupFindInterface } from '@zaoblako/shared/modules/security/dtos/user-group/user-group-find.dto';
import type { BillFindInterface } from '@zaoblako/shared/modules/document/dtos/bill/bill-find.dto';
import type { CurrencyFindInterface } from '@zaoblako/shared/modules/catalog/dtos/currency/currency-find.dto';

import * as yup from 'yup';

/** Planned income create/update APIs request body - DTO signature */
export interface PlannedIncomeFormInterface {
  /** Date of planned income */
  date: Date;
  /** Income amount in rubles */
  amount: number;
  /** Related company */
  company: CompanyFindInterface;
  /** Related direction */
  direction?: DirectionFindInterface;
  /** Related unit */
  unit?: UnitFindInterface;
  /** Related filial */
  filial?: FilialFindInterface;
  /** Related deal */
  deal?: DealFindInterface;
  /** Related counterparty */
  contragent?: ContragentFindInterface;
  /** Is related income documents was received */
  incomesReceived?: boolean;
  /** Planned income responsible user */
  responsible?: UserFindInterface;
  /** Planned income user auditors */
  userAuditors?: UserFindInterface[];
  /** Planned income user auditors */
  userGroupAuditors?: UserGroupFindInterface[];
  /** Related bill */
  bill?: BillFindInterface;
  /** Related currency */
  currency?: CurrencyFindInterface;
}

/** Planned income create/update APIs request body - DTO schema */
export const PlannedIncomeFormDto = SerdeDtoClass.create<PlannedIncomeFormInterface>(TaskDtoKeysEnum.PLANNED_INCOME_FORM_DTO, {
  date: {
    label: 'Дата',
    serializer: yup.date(),
  },
  amount: {
    label: 'Сумма',
    serializer: yup
      .number()
      .positive(DtoMessageEnum.NOT_POSITIVE),
  },
  company: {
    label: 'Компания',
    serializer: entityLikeSchema,
    checkers: [ClientCheckerKeysEnum.COMPANY_ASSERT_EXISTS_BY_DTO],
  },
  direction: {
    optional: context => !context?.data?.requiredAnalyticalFeatures?.directionsRequired,
    label: 'БС',
    serializer: entityLikeSchema,
    checkers: [CatalogCheckersEnum.DIRECTION_ASSERT_EXISTS_BY_DTO],
  },
  unit: {
    optional: context => !context?.data?.requiredAnalyticalFeatures?.unitsRequired,
    label: 'БЕ',
    serializer: entityLikeSchema,
    checkers: [CatalogCheckersEnum.UNIT_ASSERT_EXISTS_BY_DTO],
  },
  filial: {
    optional: context => !context?.data?.requiredAnalyticalFeatures?.filialsRequired,
    label: 'Филиал',
    serializer: entityLikeSchema,
    checkers: [CatalogCheckersEnum.FILIAL_ASSERT_EXISTS_BY_DTO],
  },
  deal: {
    optional: true,
    label: 'Сделка',
    serializer: entityLikeSchema,
    checkers: [BusinessCheckerKeysEnum.DEAL_ASSERT_EXISTS_BY_DTO],
  },
  contragent: {
    optional: true,
    label: 'Контрагент',
    serializer: entityLikeSchema,
    checkers: [ClientCheckerKeysEnum.CONTRAGENT_ASSERT_EXISTS_BY_DTO],
  },
  incomesReceived: {
    optional: true,
    label: 'Документы для поступления найдены',
    serializer: yup.boolean(),
  },
  responsible: {
    optional: true,
    label: 'Ответственный',
    serializer: entityLikeSchema,
    checkers: [SecurityBackendValidatorsEnum.USER_ASSERT_EXISTS_BY_DTO],
  },
  userAuditors: {
    optional: true,
    label: 'Пользователи-аудиторы',
    serializer: yup.array(entityLikeSchema),
    checkers: [SecurityBackendValidatorsEnum.USER_ASSERT_EXISTS_BY_DTO],
  },
  userGroupAuditors: {
    optional: true,
    label: 'Группы-аудиторы',
    serializer: yup.array(entityLikeSchema),
    checkers: [SecurityBackendValidatorsEnum.USER_GROUP_ASSERT_EXISTS_BY_DTO],
  },
  bill: {
    optional: true,
    label: 'Счет',
    serializer: entityLikeSchema,
    checkers: [DocumentCheckersEnum.BILL_ASSERT_EXISTS_BY_DTO],
  },
  currency: {
    optional: true,
    label: 'Валюта',
    serializer: entityLikeSchema,
    checkers: [CatalogCheckersEnum.CURRENCY_ASSERT_EXISTS_BY_DTO],
  },
});
